/**
 * A button to switch to and from the list of notifications. Used in both
 * UserDropdown and the new responsive nav.
 */

import {t} from "@lingui/core/macro";
import {css, StyleSheet, type CSSProperties} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";

import NotificationsBadge from "./notifications-badge.tsx";

import caretLeftIcon from "@phosphor-icons/core/regular/caret-left.svg";

type Props = {
    buttonStyles: Array<CSSProperties>;
    direction?: "left" | "right";
    notificationCount: number;
    onClick?: () => unknown;
    // Used to close the dropdown menu this element exists in.
    onEscape?: () => unknown;
    // Used to navigate the dropdown menu this element exists in.
    onArrowDown?: () => unknown;
    // Used to navigate the dropdown menu this element exists in.
    onArrowUp?: () => unknown;
    // Used to set this button as a ref to a parent element for the purpose of focusing.
    innerRef?: (arg1?: HTMLElement | null | undefined) => unknown;
};

class NotificationsSwitch extends React.Component<Props> {
    static defaultProps: {
        buttonStyles: Array<any>;
    } = {
        buttonStyles: [],
    };

    handleClick: (event: React.SyntheticEvent) => void = (
        event: React.SyntheticEvent,
    ) => {
        const {onClick} = this.props;
        if (onClick) {
            event.preventDefault();
            onClick();
        }
    };

    // TODO(diedra): Consider writing a shared utility that can handle this up-down
    // navigation as this is used in every dropdown menu.
    handleKeyDown: (event: React.KeyboardEvent) => void = (
        event: React.KeyboardEvent,
    ) => {
        const {onClick, onEscape, onArrowDown, onArrowUp} = this.props;

        if ((event.key === "Enter" || event.key === " ") && onClick) {
            event.preventDefault();
            onClick();
        } else if (event.key === "Escape" && onEscape) {
            event.preventDefault();
            onEscape();
        } else if (event.key === "Tab" && onEscape) {
            // We explicitly don't use event.preventDefault() here because we want
            // the user to tab to the next element.
            onEscape();
        } else if (event.key === "ArrowDown" && onArrowDown) {
            event.preventDefault();
            onArrowDown();
        } else if (event.key === "ArrowUp" && onArrowUp) {
            event.preventDefault();
            onArrowUp();
        }
    };

    render(): React.ReactNode {
        const {buttonStyles, direction, notificationCount} = this.props;

        return (
            <li className={css(styles.listItem)}>
                <button
                    ref={this.props.innerRef}
                    onClick={this.handleClick}
                    onKeyDown={this.handleKeyDown}
                    className={css(
                        styles.notAButton,
                        styles.switch,
                        ...buttonStyles,
                    )}
                    data-testid="header-notifications-switch"
                >
                    {direction === "left" && (
                        <div
                            className={css(
                                styles.switchArrow,
                                styles.switchArrowLeft,
                            )}
                        >
                            <PhosphorIcon
                                icon={caretLeftIcon}
                                size="medium"
                                color={color.offBlack}
                            />
                        </div>
                    )}

                    <View style={styles.content}>
                        <LabelLarge>{t`Notifications`}</LabelLarge>
                        <Strut size={spacing.xxSmall_6} />
                        {notificationCount > 0 && (
                            <NotificationsBadge size="large" />
                        )}
                    </View>

                    <Spring />

                    {direction === "right" && (
                        <div
                            className={css(
                                styles.switchArrow,
                                styles.switchArrowRight,
                            )}
                        >
                            <PhosphorIcon
                                icon={caretLeftIcon}
                                size="medium"
                                color={color.offBlack}
                            />
                        </div>
                    )}
                </button>
            </li>
        );
    }
}

const styles = StyleSheet.create({
    // Used to override any default browser button styling.
    notAButton: {
        background: "transparent",
        border: "none",
        padding: "none",
        width: "100%",
        textAlign: "left",
    },

    listItem: {
        // This margin gives this button enough space to show the full focus outline.
        paddingTop: 1,
        backgroundColor: color.white,
        listStyleType: "none",
    },

    switch: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        borderBottom: `1px solid ${color.offBlack16}`,
        boxSizing: "border-box",
        padding: `${spacing.medium_16}px`,
        color: color.darkBlue,
        cursor: "pointer",

        ":hover": {
            color: color.blue,
            textDecoration: "underline",
        },
    },

    content: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    },

    switchArrow: {
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
    },

    switchArrowLeft: {
        marginRight: 24,
    },

    switchArrowRight: {
        transform: "scaleX(-1)",
    },
});

export default NotificationsSwitch;
